import { Component } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { BeneListService } from '../../../services/beneList.service';
import { ModalService } from '../../../services/modal.service';
import { IChild } from '../../../interfaces/policy-admin-interfaces/child.interface';
import { Beneficiary } from '../../../interfaces/policy-admin-interfaces/beneficiary.interface';
import { BeneChild } from '../../../interfaces/policy-admin-interfaces/bene-child.interface';
import { ModalRouteService } from '../../../services/modal-route.service';
import { Utils } from '../../../../../Utils';
import { FormStateService } from '../../../../../common/services/form-state.service';
import { ValidatorService } from '../../../../../../angular-shared/services/validators-reactive.service';

@Component({
  selector: 'bene-form-children',
  templateUrl: './bene-form.children.component.html',
})
export class BeneFormChildrenComponent {
  beneForm: FormGroup;
  bene: Beneficiary;
  beneficiaryType: string;
  type: string;
  percent: number;
  partner: string;
  specificChildren: boolean;
  edit: boolean = false;
  childPercentage: number[];
  deletedChildrenList: BeneChild[] = [];
  unevenChildRatios: boolean = false;

  invalidPhone: Set<number>;
  invalidSSN: Set<number>;
  invalidDate: Set<number>;
  invalidZip: Set<number>;
  invalidEmail: Set<number>;

  dobMask = [/^([0-1])/, /\d/, '/', /^(0|1|2|3)/, /\d/, '/', /^(1|2)/, /^(9|0)/, /\d/, /\d/];
  ssnMask = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  phoneMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];

  childTypes = Utils.getChildTypes();
  perStirpeOptions = Utils.getPerStirpesOptions();
  stateList = Utils.getStateList();
  widgetType: string;
  cityList: string[];
  isZipLoading: boolean = false;

  constructor(
    protected beneListService: BeneListService,
    private modalRouteService: ModalRouteService,
    private router: Router,
    private activatedRouter: ActivatedRoute,
    private fb: FormBuilder,
    public modalService: ModalService
  ) {
    this.widgetType = this.beneListService.getChannelName();

    this.bene = this.beneListService.getCurrentBene() as Beneficiary;
    this.type = this.beneListService.getPrimaryOrContingent();
    this.percent = this.bene.ratio;

    if (this.bene.dependentBenes) {
      this.edit = true;
    }

    let childrenType = 'all';
    if (
      this.bene.beneficiaryType === '11' ||
      this.bene.beneficiaryType === '11-1' ||
      this.bene.beneficiaryType === '11-2' ||
      this.bene.beneficiaryType === '12' ||
      this.bene.beneficiaryType === '12-1' ||
      this.bene.beneficiaryType === '12-2'
    ) {
      childrenType = 'marriage';
      this.specificChildren = true;
      this.partner =
        this.bene.relatedParty.partyInfo.person.firstName +
        ' ' +
        this.bene.relatedParty.partyInfo.person.middleName +
        ' ' +
        this.bene.relatedParty.partyInfo.person.lastName;
    }

    this.invalidDate = new Set();
    this.invalidPhone = new Set();
    this.invalidSSN = new Set();
    this.invalidZip = new Set();
    this.invalidEmail = new Set();

    this.beneForm = this.fb.group({
      parentFirstName: this.bene.relatedParty ? this.bene.relatedParty.partyInfo.person.firstName : '',
      parentMiddleName: this.bene.relatedParty ? this.bene.relatedParty.partyInfo.person.middleName : '',
      parentLastName: this.bene.relatedParty ? this.bene.relatedParty.partyInfo.person.lastName : '',
      childrenType: [childrenType],
      perStirpe: [this.bene.beneficiaryType.indexOf('-2') === -1 ? 'perstirpeYes' : 'perstirpeNo'],
    });

    this.SortAndCollectChildren();
  }

  //Separate deleted children from created/modified to readd on submit
  SortAndCollectChildren() {
    const oldBeneList = this.bene.dependentBenes ? this.bene.dependentBenes : [];
    const childList: IChild[] = [];

    if (oldBeneList.length > 0) {
      let lastRatio = oldBeneList[0].ratio;

      for (let i = 0; i < oldBeneList.length; i++) {
        const oldBene = this.bene.dependentBenes[i];
        if (this.bene.dependentBenes[i].action === 'deleted') {
          this.deletedChildrenList.push(oldBene);
        } else {
          childList.push(this.toiChild(oldBene));

          if (Math.abs(lastRatio - oldBene.ratio) > 0.011) {
            this.unevenChildRatios = true;
            lastRatio = oldBene.ratio;
          }
        }
      }
    }

    const childFGs = childList.map((child) => this.fb.group(child));
    const childFormArray = this.fb.array(childFGs);
    this.beneForm.setControl('childrenList', childFormArray);
  }

  get childrenList(): FormArray {
    return this.beneForm.get('childrenList') as FormArray;
  }

  addChild(): void {
    this.childrenList.push(this.fb.group(new IChild()));
  }

  toiChild(oldBene: BeneChild): IChild {
    const toReturn: IChild = new IChild();
    const person = oldBene.partyInfo.person;
    const address = oldBene.partyInfo.person.residentialAddress;
    const birthday =
      person.dob.toString().indexOf('T') !== -1
        ? person.dob.toString().substring(0, person.dob.toString().indexOf('T'))
        : person.dob;

    const returnChild: IChild = {
      id: oldBene.id,
      firstName: person.firstName,
      middleName: person.middleName,
      lastName: person.lastName,
      phone: oldBene.partyInfo.phone.number,
      dateOfBirth: birthday,
      street: FormStateService.cleanAddressString(address.street),
      line1: FormStateService.cleanAddressString(address.line1),
      line2: FormStateService.cleanAddressString(address.line2),
      city: FormStateService.cleanAddressString(address.city),
      state: FormStateService.cleanAddressString(address.state),
      zip: FormStateService.cleanAddressString(address.zip),
      ssn: person.govtId,
      ratio: oldBene.ratio * 100,
      email: oldBene.partyInfo.email,
    };

    return returnChild;
  }

  getSelectedChildOption(): void {
    if (this.beneForm.get('childrenType').value === 'all') {
      this.specificChildren = false;
    } else {
      this.specificChildren = true;
    }
  }

  checkSave() {
    if (this.unevenChildRatios) {
      this.modalService.showChildModal = true;
    } else {
      this.saveBene();
    }
  }

  continueSave() {
    if (this.modalService.saveChildren) {
      this.saveBene();
    }
  }

  saveBene(): void {
    const id = this.bene.id;
    const dependentBenes = this.makeChildList().concat(this.deletedChildrenList);
    this.bene = {
      beneficiaryPartyType: 'children',
      action: 'created',
      actionDate: this.bene.actionDate,
      id: id,
      beneficiaryType: this.getBeneficiaryType(),
      dependentBenes: dependentBenes,
      ratio: this.bene.ratio,
    };

    if (this.beneForm.get('parentFirstName').value) {
      const relatedParty = {
        //party person
        partyInfo: {
          name: "Insured's Children",
          person: {
            firstName: this.beneForm.get('parentFirstName').value,
            middleName: this.beneForm.get('parentMiddleName').value,
            lastName: this.beneForm.get('parentLastName').value,
          },
        },
      };
      this.bene.relatedParty = relatedParty;
    }

    if (this.edit) {
      this.bene.action = 'modified';
      this.beneListService.updateBene(this.bene, this.type);
    } else {
      this.beneListService.addBeneList(this.bene, this.type);
    }

    this.modalRouteService.changeRoute('bene-list');
  }

  getBeneficiaryType(): string {
    let perstirpeText = '';
    if (this.beneForm.get('perStirpe').value === 'perstirpeYes') {
      perstirpeText = '-1';
    }
    if (this.beneForm.get('perStirpe').value === 'perstirpeNo') {
      perstirpeText = '-2';
    }
    return (this.beneForm.get('childrenType').value === 'all' ? '15' : '12') + perstirpeText;
  }

  makeChildList(): Array<BeneChild> {
    this.childPercentage = this.getChildPercentages(this.beneForm.get('childrenList').value.length);
    const toReturn: BeneChild[] = [];
    for (let i = 0; i < this.beneForm.get('childrenList').value.length; i++) {
      const name =
        '' +
        this.beneForm.get('childrenList').value[i].firstName +
        ' ' +
        this.beneForm.get('childrenList').value[i].middleName +
        ' ' +
        this.beneForm.get('childrenList').value[i].lastName;

      //check id lists to see if already exists
      const id = this.beneForm.get('childrenList').value[i].id ? this.beneForm.get('childrenList').value[i].id : null;
      const action = id === null ? 'created' : 'modified';

      toReturn.push({
        beneficiaryPartyType: 'person',
        ratio: this.childPercentage[i],
        action: action,
        id: id,
        partyInfo: {
          name: name,
          person: {
            firstName: this.beneForm.get('childrenList').value[i].firstName,
            middleName: this.beneForm.get('childrenList').value[i].middleName,
            lastName: this.beneForm.get('childrenList').value[i].lastName,
            dob: this.beneForm.get('childrenList').value[i].dateOfBirth,
            govtId: this.beneForm.get('childrenList').value[i].ssn,
            govtIdType: 'ssn',
            residentialAddress: {
              street: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].street),
              line1: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].line1),
              line2: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].line2),
              city: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].city),
              state: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].state),
              zip: FormStateService.cleanAddressString(this.beneForm.get('childrenList').value[i].zip),
            },
          },
          phone: {
            number: this.beneForm.get('childrenList').value[i].phone,
          },
          email: this.beneForm.get('childrenList').value[i].email,
        },
      });
    }
    return toReturn;
  }

  getChildPercentages(numberChildren: number): number[] {
    const toReturn = [];
    if (
      numberChildren === 1 ||
      numberChildren === 2 ||
      numberChildren === 4 ||
      numberChildren === 5 ||
      numberChildren === 8 ||
      numberChildren === 10
    ) {
      for (let i = 0; i < numberChildren; i++) {
        toReturn.push(1 / numberChildren);
      }
    }
    if (numberChildren === 3 || numberChildren === 9) {
      const percent = Math.floor(100 / numberChildren) / 100;
      toReturn.push(percent + 0.01);
      for (let i = 1; i < numberChildren; i++) {
        toReturn.push(percent);
      }
    }
    if (numberChildren === 6 || numberChildren === 7) {
      const percent = Math.floor(100 / numberChildren) / 100;
      toReturn.push(percent);
      toReturn.push(percent);
      for (let i = 2; i < numberChildren; i++) {
        toReturn.push(percent + 0.01);
      }
    }
    return toReturn;
  }

  formatPhone(index: number): void {
    const phoneNum = this.beneForm.get('childrenList').value[index].phone;
    if (phoneNum) {
      const parsedNumber = phoneNum.replace(/[\s-x.+()]/g, '');

      const isValidPhoneNumber = Utils.validatePhone(parsedNumber);
      if (!isValidPhoneNumber) {
        this.invalidPhone.add(index);

        return;
      }

      let formatted = parsedNumber;
      if (parsedNumber.length === 10) {
        formatted =
          '(' + parsedNumber.substring(0, 3) + ') ' + parsedNumber.substring(3, 6) + '-' + parsedNumber.substring(6);
      } else if (parsedNumber.length > 10) {
        formatted =
          '(' +
          parsedNumber.substring(0, 3) +
          ') ' +
          parsedNumber.substring(3, 6) +
          '-' +
          parsedNumber.substring(6, 10) +
          ' x' +
          parsedNumber.substring(10);
      }

      this.beneForm.get('childrenList').value[index].phone = formatted;
      const elemId = 'phone-' + index;
      (<HTMLInputElement>document.getElementById(elemId)).value = formatted;
    }

    this.clearChildValidation(index, this.invalidPhone);
  }

  formatSSN(index: number, input): void {
    const ssnNum = this.beneForm.get('childrenList').value[index].ssn;
    if (ssnNum) {
      if (ssnNum !== '' && !ssnNum.match(/^(\+?(\d{1}|\d{2}|\d{3})[- ]?)?\d{3}[-. ]?\d{2}[-. ]?\d{4}$/)) {
        this.invalidSSN.add(index);

        return;
      }
      const numbers = ssnNum.replace(/\D/g, '');
      const formatted = numbers.substring(0, 3) + '-' + numbers.substring(3, 5) + '-' + numbers.substring(5);
      if (input) {
        input.target.value = formatted;
      } else {
        const elemId = 'ssn-' + index;
        (<HTMLInputElement>document.getElementById(elemId)).value = formatted;
      }
      this.beneForm.get('childrenList').value[index].ssn = formatted;
    }

    this.clearChildValidation(index, this.invalidSSN);
  }

  formatGovtIdEdit(govtid: string, idtype: string) {
    return Utils.formatGovtIdEdit(govtid, idtype);
  }

  formatPhoneEdit(phone: string) {
    return Utils.formatPhoneEdit(phone);
  }

  validateDate(index: number): boolean {
    const dateOfBirth = this.beneForm.get('childrenList').value[index].dateOfBirth;
    const childDob = new Date(dateOfBirth);
    if (childDob.toString() === 'Invalid Date') {
      this.invalidDate.add(index);

      return true;
    }
    const year = childDob.getFullYear().toString();
    if (Number.parseFloat(year) < 1900) {
      this.invalidDate.add(index);

      return true;
    }
    if (year.length > 4) {
      this.invalidDate.add(index);

      return true;
    }

    const today = new Date();
    if (childDob > today) {
      this.invalidDate.add(index);

      return true;
    }

    this.clearChildValidation(index, this.invalidDate);
    return false;
  }

  validateEmail(index: number): boolean {
    const email = this.beneForm.get('childrenList').value[index].email;
    const validEmail = ValidatorService.isEmailValid(email);
    if (!validEmail) {
      this.invalidEmail.add(index);
      return true;
    }

    this.clearChildValidation(index, this.invalidEmail);
    return false;
  }

  isDisabled(): boolean {
    if (this.beneForm.invalid || this.isZipLoading) {
      return true;
    }
    //different cases to validate if edit or if add
    if (this.beneForm.get('childrenList').value.length === 0) {
      return true;
    }
    if (this.bene.relatedParty) {
      if (
        this.beneForm.get('parentFirstName').value === null &&
        this.beneForm.get('childrenType').value === 'marriage'
      ) {
        return true;
      }
      if (this.beneForm.get('parentFirstName').value === '' && this.beneForm.get('childrenType').value === 'marriage') {
        return true;
      }
    }
    for (let i = 0; i < this.beneForm.get('childrenList').value.length; i++) {
      const child = this.beneForm.get('childrenList').value[i] as IChild;
      if (!child.firstName || !child.dateOfBirth || this.invalidFields(child, i)) {
        return true;
      }

      //only continue if a field has been editted
      if (
        (child.street !== '' ||
          child.line1 !== '' ||
          child.line2 !== '' ||
          child.state !== '' ||
          child.zip !== '' ||
          child.city !== '') &&
        (child.street || child.line1 || child.line2 || child.state || child.zip || child.city)
      ) {
        //if one of these is filled out, all must be filled out
        if (
          !(child.street !== '' && child.state !== '' && child.zip !== '' && child.city !== '') ||
          !(child.street && child.state && child.zip && child.city)
        ) {
          return true;
        }
      }
    }
    if (this.edit) {
      //check to see if any fields have changed
      if (
        (this.bene.beneficiaryType.indexOf('-1') !== -1 ? 'perstirpeYes' : 'perstirpeNo') !==
        this.beneForm.get('perStirpe').value
      ) {
        return false;
      }
      if (this.bene.relatedParty) {
        if (
          this.beneForm.get('parentFirstName').value !== this.bene.relatedParty.partyInfo.person.firstName ||
          this.beneForm.get('parentMiddleName').value !== this.bene.relatedParty.partyInfo.person.middleName ||
          this.beneForm.get('parentLastName').value !== this.bene.relatedParty.partyInfo.person.lastName
        ) {
          return false;
        }
      }

      const newChildList = this.makeChildList();
      return this.arraysEqual(newChildList, this.bene.dependentBenes);
    }

    return false;
  }

  arraysEqual(a: BeneChild[], b: BeneChild[]): boolean {
    if (a === b) {
      return true;
    }
    if (a === null || b === null) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; ++i) {
      const birthday1 =
        a[i].partyInfo.person.dob.indexOf('T') !== -1
          ? a[i].partyInfo.person.dob.substring(0, a[i].partyInfo.person.dob.indexOf('T'))
          : a[i].partyInfo.person.dob;
      const birthday2 =
        b[i].partyInfo.person.dob.indexOf('T') !== -1
          ? b[i].partyInfo.person.dob.substring(0, b[i].partyInfo.person.dob.indexOf('T'))
          : b[i].partyInfo.person.dob;

      if (
        a[i].partyInfo.person.firstName !== b[i].partyInfo.person.firstName ||
        a[i].partyInfo.person.middleName !== b[i].partyInfo.person.middleName ||
        a[i].partyInfo.person.lastName !== b[i].partyInfo.person.lastName ||
        birthday1 !== birthday2 ||
        a[i].partyInfo.person.residentialAddress.street !== b[i].partyInfo.person.residentialAddress.street ||
        a[i].partyInfo.person.residentialAddress.line1 !== b[i].partyInfo.person.residentialAddress.line1 ||
        a[i].partyInfo.person.residentialAddress.line2 !== b[i].partyInfo.person.residentialAddress.line2 ||
        Utils.formatGovtIdEdit(a[i].partyInfo.person.govtId, 'ssn') !==
          Utils.formatGovtIdEdit(b[i].partyInfo.person.govtId, 'ssn') ||
        Utils.formatPhoneEdit(a[i].partyInfo.phone.number) !== Utils.formatPhoneEdit(b[i].partyInfo.phone.number) ||
        a[i].partyInfo.person.residentialAddress.city !== b[i].partyInfo.person.residentialAddress.city ||
        a[i].partyInfo.person.residentialAddress.state !== b[i].partyInfo.person.residentialAddress.state ||
        a[i].partyInfo.person.residentialAddress.zip !== b[i].partyInfo.person.residentialAddress.zip ||
        a[i].partyInfo.email !== b[i].partyInfo.email
      ) {
        return false;
      }
    }
    return true;
  }

  invalidFields(child: IChild, index: number): boolean {
    if (child.dateOfBirth) {
      if (this.validateDate(index)) {
        return true;
      }
    }
    if (child.phone && !Utils.validatePhone(child.phone.replace(/[\s-x.+()]/g, ''))) {
      return true;
    }
    if (child.ssn && !child.ssn.match(/^(\+?(\d{1}|\d{2}|\d{3})[- ]?)?\d{3}[-. ]?\d{2}[-. ]?\d{4}$/)) {
      return true;
    }
    if (child.zip && !child.zip.match(/^\d{5}$/)) {
      return true;
    }
    if (child.email && this.validateEmail(index)) {
      return true;
    }
    return false;
  }

  clearChildValidation(index: number, validation: Set<number>): void {
    if (validation.has(index)) {
      validation.delete(index);
    }
  }

  clearChildAndUpdateValidation(index: number, validation: Set<number>): void {
    this.clearChildValidation(index, validation);

    // reset indexes to persist validation properly if child is deleted
    const validationCopy = new Set(validation);
    validationCopy.forEach((key, value, set) => {
      if (value !== 0) {
        validation.delete(value);
        value--;
      }
      validation.add(value);
    });
  }

  clearDeletedChildValidation(index: number): void {
    this.clearChildAndUpdateValidation(index, this.invalidDate);
    this.clearChildAndUpdateValidation(index, this.invalidZip);
    this.clearChildAndUpdateValidation(index, this.invalidPhone);
    this.clearChildAndUpdateValidation(index, this.invalidSSN);
  }

  deleteChild(index: number): void {
    const deletedChild = this.beneForm.get('childrenList').value[index];
    const id = this.beneForm.get('childrenList').value[index].id;
    if (id) {
      this.deletedChildrenList.push({
        beneficiaryPartyType: 'person',
        ratio: 0,
        action: 'deleted',
        id: id,
        partyInfo: {
          name: deletedChild.name,
          person: {
            firstName: deletedChild.firstName,
            middleName: deletedChild.middleName,
            lastName: deletedChild.lastName,
            dob: deletedChild.dateOfBirth,
            govtId: deletedChild.ssn,
            govtIdType: 'ssn',
            residentialAddress: {
              street: FormStateService.cleanAddressString(deletedChild.street),
              line1: FormStateService.cleanAddressString(deletedChild.line1),
              line2: FormStateService.cleanAddressString(deletedChild.line2),
              city: FormStateService.cleanAddressString(deletedChild.city),
              state: FormStateService.cleanAddressString(deletedChild.state),
              zip: FormStateService.cleanAddressString(deletedChild.zip),
            },
          },
          phone: {
            number: deletedChild.phone,
          },
        },
      });
    }
    this.childrenList.removeAt(index);
    this.clearDeletedChildValidation(index);
  }

  resetAddress(index: number): void {
    FormStateService.resetAddress(
      //Explicit typing is required as get/at methods return abstract controls
      (this.beneForm.get('childrenList') as FormArray).at(index) as FormGroup,
      index.toString()
    );

    this.clearChildValidation(index, this.invalidZip);
  }

  cancel(): void {
    this.modalRouteService.changeRoute('bene-list');
  }
}
